/**
 * Handling of the authorization code the jö SSO round-trip appends to the return url.
 *
 * The code is single use: it is removed from the address bar before the establish mutation runs, so
 * a reload cannot replay it and the customer never copies it into a shared link. Removal uses
 * `history.replaceState` rather than a router navigation on purpose - the page must keep its already
 * fetched data instead of re-running every query on the overview.
 */

/** Query parameter the jö sso round-trip appends to the return url. */
export const AUTHORIZATION_CODE_PARAM = 'code';

/** Strips a leading `?` and any accidental fragment before parsing. */
const toSearchParams = (search: string) =>
  new URLSearchParams(search.replace(/^\?/, '').split('#')[0]);

/**
 * Reads the authorization code from a `location.search` string.
 *
 * Returns `null` for a missing or empty code so callers can treat "no round-trip in progress" and
 * "unusable code" the same way.
 */
export const parseAuthorizationCode = (search: string): string | null => {
  if (!search) {
    return null;
  }

  return toSearchParams(search).get(AUTHORIZATION_CODE_PARAM) || null;
};

/**
 * Removes the authorization code from the current address bar, preserving every other query
 * parameter and the fragment. Does nothing when no code is present.
 */
export const stripAuthorizationCode = () => {
  const { pathname, search, hash } = window.location;
  const params = toSearchParams(search);

  if (!params.has(AUTHORIZATION_CODE_PARAM)) {
    return;
  }

  params.delete(AUTHORIZATION_CODE_PARAM);
  const remaining = params.toString();

  window.history.replaceState(
    {},
    document.title,
    `${pathname}${remaining ? `?${remaining}` : ''}${hash}`,
  );
};
